import { useCallback, useEffect, useRef, useState } from "react";
import { FaCheck, FaChevronDown, FaEnvelope, FaShieldHalved, FaTrash, FaUserPlus } from "react-icons/fa6";
import { apiFetch, apiJson, apiPostJson } from "../lib/api";
import { useOrganization } from "../context/OrganizationContext";
import { toast } from "react-toastify";

type Role = "read" | "edit" | "owner";
type Member = {
  id: number;
  user_id: number;
  name: string | null;
  email: string;
  image: string | null;
  role: Role;
  created_at: string;
};
type Invitation = {
  id: number;
  email: string;
  role: Role;
  created_at: string;
  expires_at: string;
};

const roleCopy: Record<Role, string> = {
  read: "Can view organization data",
  edit: "Can view and edit business data",
  owner: "Full access, including team management",
};

const roles: Role[] = ["read", "edit", "owner"];

function RoleDropdown({
  value,
  onChange,
  disabled = false,
  compact = false,
}: {
  value: Role;
  onChange: (role: Role) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`relative ${compact ? "w-full sm:w-40" : "w-full"}`}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-left shadow-sm transition hover:border-teal-300 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-60"
      >
        <span>
          <span className="block text-sm font-semibold capitalize text-slate-700">{value}</span>
          {!compact && <span className="block text-[11px] font-normal text-slate-400">{roleCopy[value]}</span>}
        </span>
        <FaChevronDown className={`flex-none text-xs text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div role="listbox" className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl shadow-slate-200/70">
          {roles.map((option) => (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={option === value}
              onClick={() => {
                setOpen(false);
                if (option !== value) onChange(option);
              }}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                option === value ? "bg-teal-50 text-teal-800" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold capitalize">{option}</span>
                <span className="block truncate text-[11px] font-normal text-slate-400">{roleCopy[option]}</span>
              </span>
              {option === value && <FaCheck className="flex-none text-xs text-teal-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrganizationManagementPage() {
  const { activeOrg, activeOrgId } = useOrganization();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("read");
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const data = await apiJson<{ members: Member[]; invitations: Invitation[] }>(
        `/api/organizations/${activeOrgId}/members`
      );
      setMembers(data.members);
      setInvitations(data.invitations);
    } catch (error: any) {
      setStatus(error?.message || "Unable to load team");
      toast.error(error?.message || "Unable to load team");
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeOrgId) return;
    setSubmitting(true);
    setStatus(null);
    try {
      await apiPostJson(`/api/organizations/${activeOrgId}/invitations`, { email, role });
      setEmail("");
      setStatus("Invitation sent. The link is valid for one hour.");
      toast.success("Invitation sent. The link is valid for one hour.");
      await load();
    } catch (error: any) {
      setStatus(error?.message || "Unable to send invitation");
      toast.error(error?.message || "Unable to send invitation");
    } finally {
      setSubmitting(false);
    }
  };

  const updateRole = async (memberId: number, nextRole: Role) => {
    if (!activeOrgId) return;
    setStatus(null);
    const response = await apiFetch(`/api/organizations/${activeOrgId}/members/${memberId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: nextRole }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setStatus(data.error || "Unable to update permission");
      toast.error(data.error || "Unable to update permission");
      return;
    }
    toast.success("Member permission updated.");
    await load();
  };

  const remove = async (memberId: number) => {
    if (!activeOrgId || !window.confirm("Remove this user from the organization?")) return;
    const response = await apiFetch(`/api/organizations/${activeOrgId}/members/${memberId}`, {
      method: "DELETE",
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setStatus(data.error || "Unable to remove member");
      toast.error(data.error || "Unable to remove member");
      return;
    }
    toast.success("Member removed.");
    await load();
  };

  const revoke = async (invitationId: number) => {
    if (!activeOrgId) return;
    await apiFetch(`/api/organizations/${activeOrgId}/invitations/${invitationId}`, {
      method: "DELETE",
    });
    toast.success("Invitation revoked.");
    await load();
  };

  return (
    <section className="grid gap-6">
      <div className="card flex flex-col justify-between gap-4 p-6 sm:flex-row sm:items-center">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">Organization</p>
          <h1 className="mt-2 font-display text-2xl font-semibold text-ink">Manage {activeOrg?.name}</h1>
          <p className="mt-1 text-sm text-slate-500">Invite users and control what they can access.</p>
        </div>
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
          <FaShieldHalved />
        </span>
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-[.85fr_1.4fr]">
        <form className="card grid gap-4 p-6" onSubmit={invite}>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700"><FaUserPlus /></span>
            <div><h2 className="font-display text-lg font-semibold">Invite a teammate</h2><p className="text-xs text-slate-500">Email link expires in one hour</p></div>
          </div>
          <label className="grid gap-1.5 text-sm font-semibold text-slate-600">
            Email address
            <input className="input" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@company.com" />
          </label>
          <label className="grid gap-1.5 text-sm font-semibold text-slate-600">
            Permission
            <RoleDropdown value={role} onChange={setRole} />
          </label>
          <button className="button gap-2" disabled={submitting}><FaEnvelope />{submitting ? "Sending…" : "Send invitation"}</button>
          {status && <p className="text-sm text-slate-600">{status}</p>}
        </form>

        <div className="card">
          <div className="border-b border-slate-100 px-6 py-5">
            <h2 className="font-display text-lg font-semibold">Team members</h2>
            <p className="text-sm text-slate-500">{members.length} active member{members.length === 1 ? "" : "s"}</p>
          </div>
          {loading ? <p className="p-6 text-sm text-slate-500">Loading team…</p> : (
            <div className="divide-y divide-slate-100">
              {members.map((member) => {
                const creator = member.user_id === activeOrg?.owner_user_id;
                return (
                  <div key={member.id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
                    {member.image ? (
                      <img src={member.image} alt={member.name || member.email} referrerPolicy="no-referrer" className="h-10 w-10 flex-none rounded-xl object-cover" />
                    ) : (
                      <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-slate-100 font-bold text-slate-600">{(member.name || member.email).charAt(0).toUpperCase()}</span>
                    )}
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{member.name || "Unnamed user"}{creator && <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Creator</span>}</p><p className="truncate text-xs text-slate-500">{member.email}</p></div>
                    <RoleDropdown value={member.role} disabled={creator} compact onChange={(nextRole) => void updateRole(member.id, nextRole)} />
                    <button type="button" disabled={creator} onClick={() => void remove(member.id)} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-25" aria-label={`Remove ${member.email}`}><FaTrash /></button>
                  </div>
                );
              })}
            </div>
          )}
          {invitations.length > 0 && (
            <div className="border-t border-slate-200 bg-slate-50/60 p-5">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Pending invitations</p>
              <div className="grid gap-2">
                {invitations.map((invitation) => (
                  <div key={invitation.id} className="flex items-center gap-3 rounded-xl bg-white p-3">
                    <FaEnvelope className="text-slate-400" /><div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{invitation.email}</p><p className="text-xs capitalize text-slate-400">{invitation.role} · expires {new Date(invitation.expires_at).toLocaleTimeString()}</p></div>
                    <button type="button" onClick={() => void revoke(invitation.id)} className="text-xs font-semibold text-red-600">Revoke</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
